import { PrivacyPolicyModalData } from "@/pages/home/components/PrivacyPolicyModal";

export const navLinks = [
  { label: "Services", href: "#solutions" },
  { label: "About", href: "#about" },
  { label: "Why Cybi", href: "#value-props" },
  { label: "Testimonials", href: "#testimonials" },
  { label: "Contact", href: "#cta" },
];

export const heroServiceBadges = [
  { icon: "ri-computer-line", label: "Managed IT", pos: "top-8 left-4" },
  {
    icon: "ri-shield-keyhole-line",
    label: "Cybersecurity",
    pos: "top-4 right-4",
  },
  {
    icon: "ri-stack-line",
    label: "SaaS",
    pos: "top-1/2 left-0 -translate-y-1/2",
  },
  {
    icon: "ri-cloud-line",
    label: "Cloud",
    pos: "top-1/2 right-0 -translate-y-1/2",
  },
  { icon: "ri-code-box-line", label: "Custom Dev", pos: "bottom-8 left-4" },
  { icon: "ri-settings-3-line", label: "Automation", pos: "bottom-8 right-4" },
];

export const valueProps = [
  {
    icon: "ri-shield-check-line",
    title: "Proactive Infrastructure Management",
    description: "We monitor and maintain your entire IT environment 24/7, preventing issues before they disrupt your business operations.",
  },
  {
    icon: "ri-code-box-line",
    title: "Custom-Built Digital Systems",
    description: "Purpose-built software tailored to your workflows, not off-the-shelf solutions forced to fit your business.",
  },
  {
    icon: "ri-lock-line",
    title: "Security-First Engineering",
    description: "Every system we build and manage is architected with defense-in-depth principles, safeguarding your data and operations.",
  },
  {
    icon: "ri-flashlight-line",
    title: "Fast Response Times",
    description: "Average response under 15 minutes. Your team stays productive with minimal downtime and rapid issue resolution.",
  },
  {
    icon: "ri-cloud-line",
    title: "Cloud-Native Expertise",
    description: "Deep experience with Microsoft 365, Azure, and modern cloud platforms to power your remote and hybrid workforce.",
  },
  {
    icon: "ri-settings-3-line",
    title: "Business Process Automation",
    description: "We identify repetitive workflows and automate them, freeing your team to focus on high-value strategic work.",
  },
  {
    icon: "ri-hand-heart-line",
    title: "Long-Term Technology Partnership",
    description: "We invest in understanding your business goals, providing strategic IT guidance that evolves as your company grows.",
  },
];

export const aboutStats = [
  {
    value: "99.9%",
    label: "Uptime SLA",
    sublabel: "Enterprise-grade reliability",
  },
  {
    value: "&lt;15 min",
    label: "Avg. Response",
    sublabel: "Rapid issue resolution",
  },
  {
    value: "20+",
    label: "Businesses Served",
    sublabel: "Across 12 industries",
  },
  { value: "24/7", label: "Monitoring", sublabel: "Always-on protection" },
];

export const solutions = [
  {
    id: "managed-it",
    icon: "ri-computer-line",
    title: "Managed IT Services",
    description: "Help desk, device management, network monitoring, Microsoft 365 administration, and strategic IT consulting.",
    category: "Infrastructure",
    longDescription:
      "We take full ownership of your IT environment — from day-to-day help desk support to long-term technology strategy. Our engineers monitor your systems around the clock, apply patches and updates proactively, and resolve issues before your team even notices them.",
    features: [
      "24/7 help desk with &lt;15 min average response",
      "Device procurement, configuration & lifecycle management",
      "Network performance monitoring & optimization",
      "Microsoft 365 administration & user management",
      "Strategic technology roadmap & quarterly business reviews",
      "Vendor management for internet, hardware & software",
    ],
  },
  {
    id: "led-displays",
    icon: "ri-lightbulb-line",
    title: "LED Displays",
    description: "Custom LED display solutions for advertising, information, and branding needs.",
    category: "Infrastructure",
    longDescription: "We design and install custom LED displays that capture attention and convey your message effectively. Whether for indoor or outdoor use, our LED solutions are built to last and deliver stunning visuals.",
    features: ["Custom design and engineering", "High brightness and visibility", "Weather-resistant construction", "Easy maintenance and operation", "Integration with existing systems"],
  },
  {
    id: "cybersecurity",
    icon: "ri-shield-keyhole-line",
    title: "Cybersecurity",
    description: "Endpoint protection, threat monitoring, MFA & identity security, security audits, and business continuity planning.",
    category: "Security",
    longDescription:
      "Cybersecurity is not an add-on — it is woven into everything we do. We deploy layered defenses across your endpoints, network, identities, and cloud services. From real-time threat detection to incident response planning, we keep your business protected against the evolving threat landscape.",
    features: [
      "Endpoint detection & response (EDR) on all devices",
      "24/7 security operations center (SOC) monitoring",
      "Multi-factor authentication deployment & enforcement",
      "Quarterly vulnerability assessments & penetration testing",
      "Security awareness training for your entire team",
      "Incident response & business continuity planning",
    ],
  },
  {
    id: "microsoft-365",
    icon: "ri-microsoft-line",
    title: "Microsoft 365 Management",
    description: "Full Microsoft 365 and Azure administration, cloud migration, backup and disaster recovery, infrastructure management.",
    category: "Cloud",
    longDescription:
      "Microsoft 365 is the backbone of modern work, but most businesses only use a fraction of what it offers. We handle everything from license optimization and security hardening to SharePoint architecture and Azure hybrid deployments — making sure your cloud environment is secure, efficient, and cost-effective.",
    features: [
      "License audit & cost optimization",
      "Exchange Online, SharePoint & Teams administration",
      "Azure Active Directory & conditional access policies",
      "Cloud-to-cloud backup with 30-day retention",
      "OneDrive & SharePoint data governance",
      "Seamless migration from on-prem or other platforms",
    ],
  },
  {
    id: "custom-websites",
    icon: "ri-window-line",
    title: "Custom Websites",
    description: "High-performance business websites, conversion-optimized landing pages, client portals, e-commerce builds, SEO-first architecture.",
    category: "Development",
    longDescription:
      "Your website should be your best salesperson. We build fast, beautiful, and accessible websites that rank well on Google and convert visitors into customers. Every site we deliver is hand-crafted with modern frameworks, optimized for Core Web Vitals, and built to scale with your business.",
    features: [
      "Custom design aligned with your brand identity",
      "Responsive across all devices & screen sizes",
      "SEO-optimized architecture with structured data",
      "Content management system for easy updates",
      "E-commerce integration with payment processing",
      "Ongoing maintenance, hosting & performance monitoring",
    ],
  },
  {
    id: "saas-dev",
    icon: "ri-stack-line",
    title: "SaaS Development",
    description: "Multi-tenant SaaS platforms, subscription systems, Stripe billing integration, scalable cloud architecture, customer portals.",
    category: "Engineering",
    longDescription:
      "From concept to launch, we build SaaS products that are secure, scalable, and delightful to use. Our engineering team handles the full stack — React and modern frameworks on the frontend, robust APIs on the backend, and cloud infrastructure that auto-scales with your user growth.",
    features: [
      "Multi-tenant architecture with data isolation",
      "Subscription management & recurring billing (Stripe)",
      "Role-based access control & team management",
      "RESTful & GraphQL APIs with developer docs",
      "Automated CI/CD pipelines & zero-downtime deploys",
      "White-label customer portals & admin dashboards",
    ],
  },
  {
    id: "automation",
    icon: "ri-git-branch-line",
    title: "Workflow Automation",
    description: "Internal business tools, CRM systems, API integrations, client management systems, and process automation platforms.",
    category: "Development",
    longDescription:
      "Manual data entry and repetitive workflows quietly drain your team's productivity. We identify these bottlenecks and build custom automation solutions — connecting your existing tools, eliminating double entry, and giving your team back hours every week to focus on what actually moves the needle.",
    features: [
      "Custom CRM & client management systems",
      "API integrations between your existing tools",
      "Automated reporting & dashboard generation",
      "Document generation & e-signature workflows",
      "Data migration & system consolidation",
      "Workflow audit to identify automation opportunities",
    ],
  },
  {
    id: "voip",
    icon: "ri-phone-line",
    title: "VoIP & Communications",
    description: "Business phone systems, call routing automation, and remote workforce communication systems for modern distributed teams.",
    category: "Infrastructure",
    longDescription:
      "Modern teams need communication tools that work everywhere — in the office, at home, and on the go. We deploy and manage VoIP systems with intelligent call routing, voicemail-to-email, video conferencing integration, and mobile apps so your team stays connected no matter where they are.",
    features: [
      "Cloud-based phone system with mobile & desktop apps",
      "Auto-attendant & intelligent call routing",
      "Voicemail-to-email & transcribed messages",
      "Video conferencing & screen sharing built-in",
      "Integration with Microsoft Teams & CRM platforms",
      "Hardware procurement & configuration (desk phones, headsets)",
    ],
  },
  {
    id: "network",
    icon: "ri-router-line",
    title: "Network Infrastructure",
    description: "Network design, implementation, and monitoring with redundant architecture for maximum uptime and performance.",
    category: "Infrastructure",
    longDescription:
      "A slow or unreliable network is a silent productivity killer. We design, implement, and manage network infrastructure with redundancy at every layer — from fiber cabling and enterprise-grade switches to VLAN segmentation and QoS policies that prioritize your critical business applications.",
    features: [
      "Network design & architecture consulting",
      "Enterprise-grade switching, routing & firewall deployment",
      "VLAN segmentation & network access control",
      "Wi-Fi 6 site surveys & wireless optimization",
      "Bandwidth monitoring & QoS traffic prioritization",
      "Redundant failover & disaster recovery networking",
    ],
  },
];

export const testimonials = [
  {
    quote:
      "Our company underwent a radical transformation after partnering with Cybi Solutions. They helped us automate many of our day-to-day processes, streamline our operations, and significantly improve the way we serve our customers. Since implementing their solutions, our customer service has improved by over 70%, and our team can now focus on growing the business instead of repetitive administrative tasks.",
    name: "Alfredo Bustos",
    role: "Owner, A Plus Construction of SW FL",
    initials: "AB",
  },

  // {
  //   quote: "We needed a partner who could both manage our IT and build our SaaS platform. Cybi delivered on both fronts. The customer portal they built for our clients increased our retention by 60% in the first quarter alone.",
  //   name: "James Morrison",
  //   role: "Founder, BuildRight Construction",
  //   initials: "JM",
  // },
  {
    quote: "Moving from our old MSP to Cybi was like upgrading from a bicycle to a spaceship. They redesigned our network, implemented zero-trust security, and built automated workflows that eliminated our manual data entry.",
    name: "Elena Vasquez",
    role: "VP Operations, Pacific Retail Group",
    initials: "EV",
  },
];

export const serviceOptions = ["Managed IT Services", "LED Display Outdoor/Indoor", "Cybersecurity", "Cloud Infrastructure", "Custom Websites", "Custom Software Development", "SaaS Development", "VoIP & Communications", "Other / Multiple"];

export const privacyPolicyData: PrivacyPolicyModalData = {
  title: "Privacy Policy",
  date: "Last updated: July 17, 2026",
  description: [
    {
      type: "paragraph",
      content: 'Cybi Solutions LLC ("Cybi Solutions," "we," "our," or "us") respects your privacy and is committed to protecting the personal information you provide when using our website and services.',
    },
    {
      type: "paragraph",
      content: "This Privacy Policy explains how we collect, use, and protect information collected through our website, contact forms, and business communications.",
    },
    {
      type: "heading",
      content: "Information We Collect",
    },
    {
      type: "paragraph",
      content: "When you contact us or request information about our services, we may collect the following information:",
    },
    {
      type: "list",
      content: ["Full Name", "Email Address", "Phone Number", "Company Name", "Service Interest", "Project Description or Message", "Any additional information you voluntarily provide"],
    },
    {
      type: "paragraph",
      content: "We only collect information that you choose to provide to us.",
    },
    {
      type: "heading",
      content: "How We Use Your Information",
    },
    {
      type: "list",
      content: [
        "Respond to inquiries and consultation requests.",
        "Understand your technology needs.",
        "Provide service information and recommendations.",
        "Prepare proposals, estimates, or service agreements.",
        "Communicate regarding potential or existing business relationships.",
        "Improve our website and services.",
      ],
    },
    {
      type: "paragraph",
      content: "If we introduce marketing communications in the future, we may use your contact information to send relevant updates, announcements, or promotional communications. You may unsubscribe from marketing communications at any time.",
    },
    {
      type: "heading",
      content: "CRM and Third-Party Services",
    },
    {
      type: "paragraph",
      content: "We use third-party service providers to help manage business operations.",
    },
    {
      type: "subheading",
      content: "HubSpot",
    },
    {
      type: "paragraph",
      content:
        "HubSpot is used as our customer relationship management (CRM) platform to store and manage contact information submitted through our website forms. Information submitted through our website may be processed and stored by HubSpot according to their privacy practices.",
    },
    {
      type: "heading",
      content: "Analytics and Cookies",
    },
    {
      type: "paragraph",
      content: "We may use cookies and similar technologies to improve website functionality, analyze website traffic, and understand how visitors interact with our website.",
    },
    {
      type: "paragraph",
      content: "At this time, we do not use Google Analytics. If analytics services are implemented in the future, we will update this Privacy Policy to describe their use.",
    },
    {
      type: "heading",
      content: "Information Sharing",
    },
    {
      type: "paragraph",
      content: "Cybi Solutions LLC does not sell, rent, or trade your personal information.",
    },
    {
      type: "paragraph",
      content:
        "We may share information with trusted third-party providers that help us operate our business, including CRM, communication, hosting, or analytics providers. These providers only receive information necessary to perform their services.",
    },
    {
      type: "heading",
      content: "Data Security",
    },
    {
      type: "paragraph",
      content: "We use reasonable administrative, technical, and organizational measures to protect personal information. However, no method of electronic transmission or storage is completely secure, and we cannot guarantee absolute security.",
    },
    {
      type: "heading",
      content: "Data Retention",
    },
    {
      type: "paragraph",
      content: "We retain personal information only for as long as necessary to fulfill the purposes described in this Privacy Policy, maintain business records, comply with legal obligations, or resolve disputes.",
    },
    {
      type: "heading",
      content: "Your Privacy Rights",
    },
    {
      type: "list",
      content: ["Request access to your information.", "Request correction of inaccurate information.", "Request deletion of your information.", "Opt out of certain communications."],
    },
    {
      type: "heading",
      content: "Children's Privacy",
    },
    {
      type: "paragraph",
      content: "Our website and services are intended for businesses and adults. We do not knowingly collect personal information from children under the age of 13.",
    },
    {
      type: "heading",
      content: "Changes to This Privacy Policy",
    },
    {
      type: "paragraph",
      content: "We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated effective date.",
    },
    {
      type: "heading",
      content: "Contact Information",
    },
    {
      type: "paragraph",
      content: "Cybi Solutions LLC\nFlorida, United States\n\nEmail: info@cybisolutions.com",
    },
  ],
};

export const termsConditionsData: PrivacyPolicyModalData = {
  title: "Terms & Conditions",
  date: "Last updated: July 17, 2026",
  description: [
    {
      type: "paragraph",
      content: "These Terms & Conditions govern your use of the Cybi Solutions LLC website and describe the terms that apply when you interact with our company and services.",
    },
    {
      type: "paragraph",
      content: "By accessing our website or submitting information through our contact forms, you agree to these Terms & Conditions. If you do not agree with these terms, please do not use our website or services.",
    },

    {
      type: "heading",
      content: "About Our Services",
    },
    {
      type: "paragraph",
      content:
        "Cybi Solutions LLC provides technology consulting and business solutions, including managed IT services, cybersecurity, cloud services, Microsoft 365 solutions, website development, SaaS development, workflow automation, communications, and network infrastructure services.",
    },
    {
      type: "paragraph",
      content: "Service descriptions provided on our website are for informational purposes only and may be updated or changed at any time.",
    },

    {
      type: "heading",
      content: "Consultation Requests",
    },
    {
      type: "paragraph",
      content: "Submitting a consultation request through our website does not create a contractual relationship between you and Cybi Solutions LLC.",
    },
    {
      type: "paragraph",
      content: "A business relationship begins only after both parties agree to the scope of work, pricing, timelines, and applicable service agreements.",
    },

    {
      type: "heading",
      content: "Quotes and Proposals",
    },
    {
      type: "paragraph",
      content: "Any estimates, proposals, recommendations, or service information provided by Cybi Solutions LLC are subject to review and may require additional information before final pricing and scope are confirmed.",
    },
    {
      type: "paragraph",
      content: "Final deliverables, responsibilities, timelines, payment terms, and project requirements will be defined in a written agreement between Cybi Solutions LLC and the client.",
    },

    {
      type: "heading",
      content: "Client Responsibilities",
    },
    {
      type: "list",
      content: [
        "Provide accurate information necessary for service delivery.",
        "Provide required access, approvals, and feedback in a timely manner.",
        "Maintain appropriate licenses and permissions for systems, software, and data provided to Cybi Solutions LLC.",
        "Communicate project requirements and changes clearly.",
      ],
    },

    {
      type: "heading",
      content: "Payments",
    },
    {
      type: "paragraph",
      content: "Payment terms, pricing, deposits, recurring fees, and billing schedules will be defined in individual proposals, agreements, or invoices.",
    },
    {
      type: "paragraph",
      content: "Failure to meet payment obligations may result in suspension or termination of services.",
    },

    {
      type: "heading",
      content: "Intellectual Property",
    },
    {
      type: "paragraph",
      content: "Unless otherwise agreed in writing, Cybi Solutions LLC retains ownership of its proprietary tools, processes, methodologies, documentation, and internal resources used to deliver services.",
    },
    {
      type: "paragraph",
      content: "Ownership rights for client-specific deliverables will be defined in applicable agreements.",
    },

    {
      type: "heading",
      content: "Limitation of Liability",
    },
    {
      type: "paragraph",
      content:
        "To the maximum extent permitted by applicable law, Cybi Solutions LLC shall not be liable for indirect, incidental, special, or consequential damages resulting from the use of our website, services, recommendations, or technology solutions.",
    },
    {
      type: "paragraph",
      content: "While we work to provide reliable and secure solutions, we cannot guarantee that systems will always be free from errors, interruptions, vulnerabilities, or security threats.",
    },

    {
      type: "heading",
      content: "Website Use",
    },
    {
      type: "list",
      content: ["Use the website only for lawful purposes.", "Do not attempt to interfere with website security or functionality.", "Do not submit false, misleading, or unauthorized information."],
    },

    {
      type: "heading",
      content: "Third-Party Links",
    },
    {
      type: "paragraph",
      content: "Our website may contain links to third-party websites or services. Cybi Solutions LLC is not responsible for the content, security, availability, or privacy practices of third-party websites.",
    },

    {
      type: "heading",
      content: "Changes to These Terms",
    },
    {
      type: "paragraph",
      content: "Cybi Solutions LLC reserves the right to update or modify these Terms & Conditions at any time. Updated terms will be posted on this page with the revised effective date.",
    },

    {
      type: "heading",
      content: "Governing Law",
    },
    {
      type: "paragraph",
      content: "These Terms & Conditions are governed by the laws of the State of Florida, United States, without regard to conflict of law principles.",
    },

    {
      type: "heading",
      content: "Contact Information",
    },
    {
      type: "paragraph",
      content: "Cybi Solutions LLC\nFlorida, United States\n\nEmail: info@cybisolutions.com",
    },
  ],
};
